import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, Lock, Unlock, Loader2, MessageSquare, Wallet } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const thisMonth = () => new Date().toISOString().slice(0, 7);
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));

const PAYOUT_BADGE = {
  approved: 'bg-green-500/20 text-green-300 border-green-500/40',
  rejected: 'bg-red-500/15 text-red-300 border-red-500/40',
  held: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  pending: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40',
};

export default function SalesPerformancePage() {
  const [tab, setTab] = useState('performance');
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'performance') {
        const r = await axios.get(`${API}/sales/performance/admin?month=${month}`, auth());
        setData(r.data);
      } else if (tab === 'payouts') {
        const r = await axios.get(`${API}/sales/payouts?month=${month}`, auth());
        setPayouts(r.data);
      } else {
        const r = await axios.get(`${API}/sales/inbox-analytics?month=${month}`, auth());
        setInbox(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tab, month]);

  useEffect(() => { load(); }, [load]);

  const payoutAction = async (employeeId, action, extra = {}) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/sales/payouts/action`, { month, employee_id: employeeId, action, ...extra }, auth());
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const toggleLock = async () => {
    if (!window.confirm(payouts?.locked ? `Unlock ${month}?` : `Lock ${month}? Approved payouts become final; adjustments go to the next month.`)) return;
    try {
      const r = await axios.post(`${API}/sales/payouts/lock`, { month, unlock: payouts?.locked }, auth());
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="sales-performance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-wt-text-primary flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-wt-gold" /> Sales Performance
          </h1>
          <p className="text-sm text-wt-text-muted">Employee sales, incentives & monthly payout closing</p>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="w-44 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="performance-month-picker" />
      </div>

      <div className="flex gap-2 border-b border-wt-border">
        {[['performance', 'Performance'], ['payouts', 'Payout Closing'], ['inbox', 'Inbox Analytics']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`perf-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? 'border-wt-gold text-wt-gold' : 'border-transparent text-wt-text-muted hover:text-wt-text-primary'}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-wt-gold" /></div>
      ) : tab === 'performance' && data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {Object.entries(data.attribution_buckets || {}).map(([k, v]) => (
              <Card key={k} className="bg-wt-surface border-wt-border">
                <CardContent className="p-3">
                  <p className="text-[11px] text-wt-text-muted capitalize">{k.replace(/_/g, ' ')}</p>
                  <p className="text-xl font-bold text-wt-text-primary">{v}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="overflow-x-auto rounded-lg border border-wt-border">
            <table className="w-full text-sm">
              <thead className="bg-wt-paper text-wt-text-muted text-xs">
                <tr>
                  {['Employee', 'Enabled', 'Target', 'Achieved', 'Achv %', 'Slab %', 'Incentive', 'Rewards', 'Penalties', 'Net', 'Orders', 'AOV', 'Cancelled', 'Returned'].map(h => (
                    <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.employees.map(r => (
                  <tr key={r.employee_id} className="border-t border-wt-border" data-testid={`perf-row-${r.employee_id}`}>
                    <td className="px-3 py-2 text-wt-text-primary whitespace-nowrap">{r.employee_name}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs ${r.incentive_enabled ? 'text-green-400' : 'text-red-400'}`}>{r.incentive_enabled ? 'Yes' : 'No'}</span>
                    </td>
                    <td className="px-3 py-2 text-wt-text-secondary">{fmt(r.target)}</td>
                    <td className="px-3 py-2 text-wt-gold font-medium">{fmt(r.achieved)}</td>
                    <td className="px-3 py-2 text-wt-text-secondary">{r.achievement_pct}%</td>
                    <td className="px-3 py-2 text-wt-text-secondary">{r.slab_pct}%</td>
                    <td className="px-3 py-2 text-emerald-400">{fmt(r.estimated_incentive)}</td>
                    <td className="px-3 py-2 text-wt-text-secondary">{fmt(r.manual_rewards)}</td>
                    <td className="px-3 py-2 text-red-400">{fmt(r.penalties)}</td>
                    <td className="px-3 py-2 text-wt-text-primary font-medium">{fmt(r.net_payable)}</td>
                    <td className="px-3 py-2 text-wt-text-secondary">{r.delivered_orders} ({r.pending_orders} pend)</td>
                    <td className="px-3 py-2 text-wt-text-secondary">{fmt(r.avg_order_value)}</td>
                    <td className="px-3 py-2 text-wt-text-secondary">{r.cancelled_orders}</td>
                    <td className="px-3 py-2 text-wt-text-secondary">{r.returned_orders}</td>
                  </tr>
                ))}
                {!data.employees.length && (
                  <tr><td colSpan={14} className="px-3 py-6 text-center text-wt-text-muted">No sales activity or enabled employees this month</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : tab === 'payouts' && payouts ? (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-wt-text-muted">
              {payouts.locked
                ? <span className="text-amber-400">Month {month} is LOCKED{payouts.lock ? ` by ${payouts.lock.locked_by}` : ''} — refunds after payout go to next month as adjustments</span>
                : 'Review each employee, approve payouts, then lock the month.'}
            </p>
            <Button size="sm" variant="outline" onClick={toggleLock} data-testid="lock-month-btn"
              className={payouts.locked ? 'border-amber-500/40 text-amber-300' : 'border-wt-border text-wt-text-primary'}>
              {payouts.locked ? <><Unlock className="w-3.5 h-3.5 mr-1" /> Unlock Month</> : <><Lock className="w-3.5 h-3.5 mr-1" /> Lock Month</>}
            </Button>
          </div>
          <div className="space-y-2">
            {payouts.rows.map(r => (
              <Card key={r.employee_id} className="bg-wt-surface border-wt-border" data-testid={`payout-row-${r.employee_id}`}>
                <CardContent className="p-3 flex flex-wrap items-center gap-3">
                  <div className="w-44 min-w-0">
                    <p className="text-sm font-medium text-wt-text-primary truncate">{r.employee_name}</p>
                    <p className={`text-[11px] ${r.eligible ? 'text-green-400' : 'text-red-400'}`}>{r.eligibility_reason}</p>
                  </div>
                  <div className="text-xs text-wt-text-muted">
                    <p>Target {fmt(r.target)} · Achieved <span className="text-wt-gold">{fmt(r.achieved)}</span> ({r.achievement_pct}%)</p>
                    <p>Slab {r.slab_pct}% → Incentive <span className="text-emerald-400">{fmt(r.estimated_incentive)}</span> · Rewards {fmt(r.manual_rewards)} · Penalties {fmt(r.penalties)} · Adj {fmt(r.adjustment_total)}</p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <div className="text-right mr-2">
                      <p className="text-[11px] text-wt-text-muted">Net Payable</p>
                      <p className="text-lg font-bold text-wt-text-primary" data-testid={`net-payable-${r.employee_id}`}>{fmt(r.net_payable)}</p>
                    </div>
                    <Badge className={`border text-xs ${PAYOUT_BADGE[r.payout_status] || PAYOUT_BADGE.pending}`}>{r.payout_status}</Badge>
                    {!payouts.locked && r.payout_status !== 'approved' && (
                      <>
                        <Button size="sm" disabled={busy} className="h-7 text-xs bg-green-500/20 text-green-300 border border-green-500/40 hover:bg-green-500/30"
                          data-testid={`approve-payout-${r.employee_id}`}
                          onClick={() => {
                            if (!r.eligible && !window.confirm(`${r.eligibility_reason}. Override and approve anyway?`)) return;
                            payoutAction(r.employee_id, 'approve', { override: !r.eligible });
                          }}>Approve</Button>
                        <Button size="sm" disabled={busy} variant="outline" className="h-7 text-xs border-amber-500/40 text-amber-300"
                          onClick={() => payoutAction(r.employee_id, 'hold', { notes: 'Held by admin' })}>Hold</Button>
                        <Button size="sm" disabled={busy} variant="outline" className="h-7 text-xs border-red-500/40 text-red-300"
                          onClick={() => payoutAction(r.employee_id, 'reject', { notes: 'Rejected by admin' })}>Reject</Button>
                        <Button size="sm" disabled={busy} variant="outline" className="h-7 text-xs border-wt-border text-wt-text-secondary"
                          data-testid={`adjust-payout-${r.employee_id}`}
                          onClick={() => {
                            const amt = window.prompt('Adjustment amount (negative to deduct):');
                            if (!amt) return;
                            const notes = window.prompt('Adjustment reason:') || '';
                            payoutAction(r.employee_id, 'adjust', { amount: parseFloat(amt), notes });
                          }}><Wallet className="w-3 h-3 mr-1" />Adjust</Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {!payouts.rows.length && <p className="text-center text-wt-text-muted py-8">No payable employees for {month}</p>}
          </div>
        </>
      ) : tab === 'inbox' && inbox ? (
        <div className="overflow-x-auto rounded-lg border border-wt-border">
          <table className="w-full text-sm">
            <thead className="bg-wt-paper text-wt-text-muted text-xs">
              <tr>
                {['Agent', 'Assigned', 'Open', 'Closed', 'Messages Sent', 'Leads Created', 'Orders Created', 'Inbox Revenue', 'Conversion %'].map(h => (
                  <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {inbox.agents.map(a => (
                <tr key={a.employee_id} className="border-t border-wt-border">
                  <td className="px-3 py-2 text-wt-text-primary flex items-center gap-1.5 whitespace-nowrap">
                    <MessageSquare className="w-3.5 h-3.5 text-wt-gold" /> {a.employee_name || a.employee_id}
                  </td>
                  <td className="px-3 py-2 text-wt-text-secondary">{a.conversations_assigned}</td>
                  <td className="px-3 py-2 text-wt-text-secondary">{a.open_conversations}</td>
                  <td className="px-3 py-2 text-wt-text-secondary">{a.closed_conversations}</td>
                  <td className="px-3 py-2 text-wt-text-secondary">{a.messages_sent}</td>
                  <td className="px-3 py-2 text-wt-text-secondary">{a.leads_created}</td>
                  <td className="px-3 py-2 text-wt-gold">{a.orders_created}</td>
                  <td className="px-3 py-2 text-emerald-400">{fmt(a.inbox_revenue)}</td>
                  <td className="px-3 py-2 text-wt-text-secondary">{a.conversion_rate}%</td>
                </tr>
              ))}
              {!inbox.agents.length && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-wt-text-muted">No inbox activity this month</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
